import { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const scrollMemory = new Map();

/** Set on location.state when goBack() navigates via returnTo (REPLACE, not POP). */
export const SCROLL_RESTORE_STATE_KEY = '__scrollRestore';

function getScrollContainer() {
  return document.getElementById('maestro-scroll-container') || null;
}

/**
 * Saves and restores scroll position for list views that use infinite paging.
 *
 * @param {string} key - Unique key for this view (include tab/query so tab/filter changes get fresh positions)
 * @param {object} opts
 * @param {boolean} opts.ready - True when enough content is loaded to restore (e.g. !loading && items.length >= savedCount)
 * @param {number} opts.itemCount - Current number of loaded items (used to decide when restore is safe)
 */
export default function useScrollRestore(key, { ready = true, itemCount = 0 } = {}) {
  const location = useLocation();
  const navType = useNavigationType();
  const restoredRef = useRef(false);
  const keyRef = useRef(key);
  keyRef.current = key;

  // Save scroll position when leaving (unmount or key change)
  const savePosition = useCallback(() => {
    const container = getScrollContainer();
    if (!container) return;
    const scrollTop = container.scrollTop;
    if (scrollTop > 0) {
      scrollMemory.set(keyRef.current, {
        scrollTop,
        itemCount,
        timestamp: Date.now(),
      });
    }
  }, [itemCount]);

  // Save on unmount
  useEffect(() => {
    return () => savePosition();
  }, [savePosition]);

  // Save when key changes (tab switch, filter change)
  const prevKeyRef = useRef(key);
  useEffect(() => {
    if (prevKeyRef.current !== key) {
      // Save position for the *previous* key before switching
      const container = getScrollContainer();
      if (container && container.scrollTop > 0) {
        scrollMemory.set(prevKeyRef.current, {
          scrollTop: container.scrollTop,
          itemCount,
          timestamp: Date.now(),
        });
      }
      prevKeyRef.current = key;
      restoredRef.current = false;
    }
  }, [key, itemCount]);

  // Restore scroll position
  useEffect(() => {
    if (restoredRef.current) return;
    if (!ready) return;

    const scrollRestoreFlag = location.state?.[SCROLL_RESTORE_STATE_KEY] === true;
    // POP = browser/history back; returnTo back uses REPLACE + scrollRestoreFlag
    if (navType !== 'POP' && !scrollRestoreFlag) {
      restoredRef.current = true;
      const container = getScrollContainer();
      if (container) container.scrollTop = 0;
      return;
    }

    const saved = scrollMemory.get(key);
    if (!saved) {
      restoredRef.current = true;
      return;
    }

    // Expire saved positions older than 30 minutes
    if (Date.now() - saved.timestamp > 30 * 60 * 1000) {
      scrollMemory.delete(key);
      restoredRef.current = true;
      return;
    }

    // Wait until enough items are loaded to make the saved scroll position reachable
    if (itemCount < saved.itemCount) return;

    restoredRef.current = true;
    const container = getScrollContainer();
    if (!container) return;

    // Use rAF to ensure layout has been computed with the new content
    requestAnimationFrame(() => {
      const maxScroll = container.scrollHeight - container.clientHeight;
      container.scrollTop = Math.min(saved.scrollTop, maxScroll);
    });
  }, [key, ready, itemCount, navType, location.state]);

  // Return a function to get the saved item count (for eager loading)
  const getSavedItemCount = useCallback(() => {
    if (navType !== 'POP') return 0;
    const saved = scrollMemory.get(key);
    return saved?.itemCount || 0;
  }, [key, navType]);

  return { getSavedItemCount };
}

export { getScrollContainer, scrollMemory };
